import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import { appStorage } from "../utils/appStorage";

import { FriendAddressPage } from "./friendComponents/addressPage";
import { FriendEducationPage } from "./friendComponents/educationPage";
import { FriendNotesPage } from "./friendComponents/notePage";
import { FriendWorkExperiencePage } from "./friendComponents/workExperiencePage";
import FriendPersonalInfoPage from "./friendComponents/personalInfoPage";
import FriendVoiceSendModal from "./friendComponents/voiceSendModal";
import { dispatchContactsChanged } from "../utils/contactListSync";
import { enforceBasicPlanNoOtherUsersAccess } from "../utils/basicPlanAccess";

const SECTION_ICONS = {
  "Personal Info": "../new-personal.svg",
  "Work Experience": "../new-work.svg",
  Education: "../new-edu.svg",
  Notes: "../note.svg",
};

const getSectionIcon = (label) => SECTION_ICONS[label] || null;

const BUTTON_STYLES = {
  base: "group flex w-full items-center gap-3 rounded-2xl border px-4 py-2 text-medium text-left transition",
  active: "border-[#6C45FF] bg-[#6C45FF] text-white shadow-[0_8px_24px_rgba(108,69,255,0.22)]",
  inactive:
    "border-[#EEE6FF] bg-white text-gray-800 hover:border-[#D6C5FF] hover:bg-[#FAF8FF] hover:shadow-[0_8px_24px_rgba(108,69,255,0.08)]",
};

const ARROW_ICONS = {
  active: "../chatbar/white-arrow.svg",
  inactive: "../chatbar/dark-arrow.svg",
};

const SectionItem = React.memo(({ label, active, onClick, icon }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    aria-label={`Open ${label}`}
    className={`${BUTTON_STYLES.base} ${active ? BUTTON_STYLES.active : BUTTON_STYLES.inactive}`}
  >
    {icon ? (
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
          active ? "bg-white" : "bg-[#F3F0FF]"
        }`}
      >
        <img src={icon} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
      </span>
    ) : null}
    <span className="min-w-0 flex-1 font-bold text-base sm:text-lg">{label}</span>
    <img
      src={ARROW_ICONS[active ? "active" : "inactive"]}
      alt=""
      aria-hidden="true"
      className="h-5 w-5 shrink-0 transition group-hover:translate-x-0.5 sm:h-6 sm:w-6"
    />
  </button>
));
const BackButton = React.memo(({ onBack, label = 'Permissions' }) => (
  <button
    type="button"
    onClick={onBack}
    aria-label={`Back to ${label}`}
    className="flex items-center aoboshi-one text-lg text-[#212121] font-medium w-max p-4 rounded-2xl !focus-visible:shadow-none"
  >
    <img src="../back-arrow.svg" alt="back-arrow" className="mr-4" />
  </button>
));
const FRIENDS_LIST_ROUTE = "/contact";

const MoonIcon = () => (
  <svg
    className="h-5 w-5 shrink-0 text-gray-900"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const normalizeUserId = (id) => String(id ?? "").trim();

const formatUpcomingEventLabel = (daysUntilText) => {
  const raw = String(daysUntilText ?? "").trim();
  if (!raw) return "";

  const normalized = raw.toLowerCase();
  if (normalized.includes("today") || /^0\s*day/.test(normalized)) return "Today";

  const dayMatch = normalized.match(/(\d+)\s*day/);
  if (dayMatch) {
    const days = Number(dayMatch[1]);
    if (days === 0) return "Today";
    if (days === 1) return "Tomorrow";
    return `In ${days} days`;
  }

  return raw;
};

const resolveFriendUpcomingEvents = (apiData, friendUserId) => {
  const targetId = normalizeUserId(friendUserId);
  if (!targetId || !apiData) {
    return { birthday: null, anniversary: null };
  }

  const birthdays = Array.isArray(apiData.birthdays) ? apiData.birthdays : [];
  const anniversaries = Array.isArray(apiData.anniversaries) ? apiData.anniversaries : [];
  const todayItems = Array.isArray(apiData.today) ? apiData.today : [];

  const birthdayFromToday = todayItems.find(
    (item) => normalizeUserId(item?.user_id) === targetId && item?.date_of_birth
  );
  const anniversaryFromToday = todayItems.find(
    (item) => normalizeUserId(item?.user_id) === targetId && item?.anniversary_date
  );

  const birthdayMatch = birthdays.find((item) => normalizeUserId(item?.user_id) === targetId);
  const anniversaryMatch = anniversaries.find((item) => normalizeUserId(item?.user_id) === targetId);

  const birthdayLabel = birthdayFromToday
    ? "Today"
    : formatUpcomingEventLabel(birthdayMatch?.days_until_birthday);
  const anniversaryLabel = anniversaryFromToday
    ? "Today"
    : formatUpcomingEventLabel(anniversaryMatch?.days_until_anniversary);

  return {
    birthday: birthdayLabel ? { label: birthdayLabel } : null,
    anniversary: anniversaryLabel ? { label: anniversaryLabel } : null,
  };
};

const UPCOMING_EVENT_ICON_FILTER =
  "brightness(0) saturate(100%) invert(36%) sepia(89%) saturate(2476%) hue-rotate(238deg) brightness(97%) contrast(101%)";

const UpcomingEventBadge = React.memo(({ type, label }) => {
  const isBirthday = type === "birthday";
  const title = isBirthday ? "Birthday" : "Anniversary";
  const icon = isBirthday ? "../ic_birthday.svg" : "../ic_ring.svg";

  return (
    <div className="inline-flex min-w-[120px] gap-2 rounded-2xl bg-[#EDE7FF] px-3 py-2.5 items-center">
      <img
        src={icon}
        alt=""
        aria-hidden="true"
        className="mt-0.5 h-[18px] w-[18px] shrink-0 object-contain"
        style={{ filter: UPCOMING_EVENT_ICON_FILTER }}
      />
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[12px] font-bold leading-tight text-[#212121] sm:text-[13px]">{title}</span>
        <span className="text-[12px] font-semibold leading-tight text-[#7C4DFF] sm:text-[13px]">{label}</span>
      </div>
    </div>
  );
});

const parseFriendStatusInfo = (statusInfo) => {
  if (!statusInfo) return null;

  const text = String(statusInfo.text || "").trim();
  const hoursText = String(statusInfo.hours_text || "").trim();

  if (!text && !hoursText) return null;

  if (text) {
    const dotIndex = text.indexOf(". ");
    if (dotIndex !== -1) {
      return {
        primary: text.slice(0, dotIndex),
        secondary:
          text.slice(dotIndex + 2).trim() ||
          "Will respond to calls and messages after this date",
      };
    }

    return {
      primary: text,
      // secondary: "Will respond to calls and messages after this date",
    };
  }

  return {
    primary: hoursText,
    // secondary: "Will respond to calls and messages after this date",
  };
};

export const UserProfile = React.memo(({ friendData, onToggleRelation, handleSectionClick, onBackFromParent, onOpenNickNameModal, loadingNickName, upcomingEvents }) => {
  const navigate = useNavigate();
  const [isVoiceModalOpen, setIsVoiceModalOpen] = React.useState(false);
  const audioRef = React.useRef(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [localRelations, setLocalRelations] = React.useState(Array.isArray(friendData?.your_friend_relations) ? friendData?.your_friend_relations : []);

  React.useEffect(() => {
    setLocalRelations(Array.isArray(friendData?.your_friend_relations) ? friendData?.your_friend_relations : []);
  }, [friendData?.your_friend_relations]);

  const handleCloseModal = () => setIsVoiceModalOpen(false);

  const rawRelations = friendData?.your_friend_relations || [];
  const voiceUrl = friendData?.voice_full_name;
  const statusDisplay = parseFriendStatusInfo(friendData?.status_info);

  const tagIcon = (tag) => {
    switch ((tag || '').toLowerCase()) {
      case 'family':
        return (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M8 11c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 20c0-2.5 4-4.5 10-4.5s10 2 10 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'friend':
        return (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M5 20c0-3 3.5-5 7-5s7 2 7 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        );
      case 'work':
        return (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="7" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2"/>
            <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'other':
        return (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 8v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="12" cy="16.5" r="0.5" fill="currentColor" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
    }
  };

  const handleTagClick = (tag) => {
    setLocalRelations((prev) => {
      const normalizedTag = String(tag || "").toLowerCase();
      const isOtherTag = normalizedTag === "other";
      let nextRelations = [];

      if (isOtherTag) {
        const alreadyOnlyOther = prev.length === 1 && prev.some((t) => String(t || "").toLowerCase() === "other");
        nextRelations = alreadyOnlyOther ? [] : [tag];
      } else {
        const withoutOther = prev.filter((t) => String(t || "").toLowerCase() !== "other");
        const exists = withoutOther.includes(tag);
        nextRelations = exists ? withoutOther.filter((t) => t !== tag) : [...withoutOther, tag];
      }

      if (onToggleRelation) onToggleRelation(tag);
      return nextRelations;
    });
  };

  const handlePlayAudio = () => {
    if (!voiceUrl) return;

    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      audioRef.current?.play();
      setIsPlaying(true);
    }
  };

  const friendEmail = String(friendData?.email || "").trim();
  const friendUserId = friendData?.user_id;
  const showBirthdayBadge = Boolean(upcomingEvents?.birthday?.label);
  const showAnniversaryBadge = Boolean(upcomingEvents?.anniversary?.label);

  const handleEmail = () => {
    if (!friendEmail) {
      toast.error("No email available");
      return;
    }
    window.location.href = `mailto:${friendEmail}`;
  };

  const handleMessage = () => {
    if (!friendUserId) return;
    navigate(`/chat/${friendUserId}`);
  };

  return (
    <div>
      <div className="relative flex items-center min-h-14 font-semibold">
        <div className="absolute left-0 top-1/2 z-10 -translate-y-1/2">
          <BackButton
            onBack={() => {
              try {
                if (typeof onBackFromParent === 'function') {
                  onBackFromParent();
                } else {
                  // Fallback: navigate to friends list
                  window.location.href = FRIENDS_LIST_ROUTE;
                }
              } catch (e) {
                window.location.href = FRIENDS_LIST_ROUTE;
              }
            }}
            label="Friends List"
          />
        </div>

        <p className="w-full text-center text-lg pointer-events-none px-14">Friend Profile</p>
      </div>

      <div className="rounded-2xl p-5">
        <div className="flex max-[1150px]:flex-col max-[1150px]:items-center items-start gap-4 md:gap-6">
          <img
            src={
              friendData?.profile_image
                ? `${process.env.REACT_APP_API_BASE_URL}${friendData?.profile_image}`
                : "./profile-picture.png"
            }
            alt={friendData?.name || "User"}
            className="h-24 w-24 md:h-28 md:w-28 rounded-full object-cover flex-shrink-0 border-2 border-[#6938EF] shadow-md"
          />
          <div className="text-sm min-w-0 space-y-2 w-full max-[900px]:flex max-[900px]:flex-col max-[900px]:justify-center max-[900px]:items-center max-[900px]:text-center">
          <div className="font-bold text-gray-900 text-base md:text-lg flex items-center gap-1.5 w-full max-[900px]:w-auto max-[900px]:max-w-full max-[900px]:justify-center">
            <span className="min-w-0 truncate" title={friendData?.name || ""}>
              {friendData?.name || "—"}
            </span>
            {friendData?.nick_name && (
              <span className="shrink-0" title={friendData.nick_name}>
                ({friendData.nick_name})
              </span>
            )}
            <div className="flex items-center gap-2 flex-shrink-0">
              {voiceUrl && (
                <button
                  onClick={handlePlayAudio}
                  className="cursor-pointer hover:opacity-70 transition"
                  title={isPlaying ? "Pause" : "Play"}
                >
                  <img
                    src={isPlaying ? "../chatscreen/pause.svg" : "../chatscreen/speaker.svg"}
                    alt={isPlaying ? "Pause" : "Play"}
                    className="w-4 h-4"
                  />
                </button>
              )}
            </div>
            <audio
              ref={audioRef}
              src={`${process.env.REACT_APP_API_BASE_URL}${voiceUrl || ""}`}
              onEnded={() => setIsPlaying(false)}
            />
          </div>

          {localRelations.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 max-[900px]:justify-center">
              {localRelations.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#7C4DFF] px-3 py-1 text-xs font-semibold text-white"
                >
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center text-white">
                    {tagIcon(tag)}
                  </span>
                  {tag}
                </span>
              ))}
            </div>
          )}

          <p className="text-gray-700 text-sm md:text-base font-medium">
            {friendData?.country_code} {friendData?.phoneNumber || "—"}
          </p>
          {/* <p className="text-gray-600 text-sm md:text-base break-all truncate">
            {friendData?.email || "—"}
          </p> */}
          <div className="relative group max-w-[260px] w-full max-[900px]:mx-auto">
            <p className="text-gray-600 text-sm md:text-base truncate overflow-hidden whitespace-nowrap cursor-pointer max-[900px]:text-center">
              {friendData?.email || ""}
            </p>

            {/* Tooltip */}
            {friendData?.email && (
              <span className="pointer-events-none absolute left-0 top-full mt-1 z-50 hidden rounded-md bg-black px-3 py-1 text-xs text-white shadow-lg group-hover:block whitespace-nowrap max-[900px]:left-1/2 max-[900px]:-translate-x-1/2">
                {friendData.email}
              </span>
            )}
          </div>

          </div>

        </div>

        {(showBirthdayBadge || showAnniversaryBadge) && (
          <div className="mt-3 flex w-full flex-row flex-wrap items-start justify-between gap-4 sm:max-w-md sm:gap-8 max-[1150px]:mx-auto max-[1150px]:justify-center">
            {showBirthdayBadge && (
              <UpcomingEventBadge type="birthday" label={upcomingEvents.birthday.label} />
            )}
            {showAnniversaryBadge && (
              <UpcomingEventBadge type="anniversary" label={upcomingEvents.anniversary.label} />
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-6 w-full justify-between max-[900px]:justify-between max-[900px]:gap-10">
        <button
          type="button"
          onClick={onOpenNickNameModal}
          disabled={loadingNickName}
          className="text-[#6366F1] text-sm font-semibold hover:underline disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loadingNickName ? "..." : "Nickname"}
        </button>
        <button
          className="text-[#6366F1] text-sm font-semibold hover:underline disabled:opacity-60 disabled:cursor-not-allowed"
          onClick={() => handleSectionClick("Notes")}
        >Add Note</button>
      </div>

      <div className="mt-4 rounded-2xl bg-white border border-gray-100 p-4 shadow-sm">
        <div className="flex items-start justify-center gap-10">
          <button
            type="button"
            onClick={handleEmail}
            disabled={!friendEmail}
            className="flex flex-col items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Email friend"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#F3F0FF]">
              <img src="../input-logos/sms.svg" alt="" aria-hidden="true" className="h-5 w-5" />
            </span>
            <span className="text-sm font-medium text-gray-700">Email</span>
          </button>

          <button
            type="button"
            onClick={handleMessage}
            disabled={!friendUserId}
            className="flex flex-col items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Message friend"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#F3F0FF]">
              <img src="../chatbox/phonebook.svg" alt="" aria-hidden="true" className="h-5 w-5" />
            </span>
            <span className="text-sm font-medium text-gray-700">Message</span>
          </button>
        </div>
      </div>

      {statusDisplay && (
        <div className="mt-4 flex items-center gap-3 rounded-2xl bg-[#fff] px-4 py-3">
          <MoonIcon />
          <div className="min-w-0">
            <p className="text-sm font-bold leading-snug text-gray-900">{statusDisplay.primary}</p>
            <p className="mt-0.5 text-xs leading-snug text-gray-800">{statusDisplay.secondary}</p>
          </div>
        </div>
      )}

      {/* Relationship Tags - Mobile only below 900px */}
      <div className="hidden max-[900px]:block mt-6 bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="text-xs font-semibold text-[#6366F1] uppercase tracking-wide">Relationship</h3>
            <div className="text-sm text-gray-600">How you know {friendData?.name || 'this person'}</div>
          </div>
        </div>

        <div className="mt-1 grid w-full grid-cols-2 gap-3">
          {["Family", "Friend", "Work", "Other"]?.map((tag) => {
            const active = localRelations.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                onClick={() => handleTagClick(tag)}
                aria-pressed={active}
                className={`flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium transition ${active ? "bg-[#6C45FF] text-white shadow-md" : "border border-gray-400 bg-white text-gray-800"}`}
              >
                <span className={`h-4 w-4 shrink-0 ${active ? "text-white" : "text-gray-500"}`}>
                  {tagIcon(tag)}
                </span>
                <span className="truncate">{tag}</span>
              </button>
            );
          })}
        </div>

        <div className="text-xs text-gray-400 mt-3">You can select multiple tags (except Other)</div>
      </div>
      {/* ✅ Relation buttons */}
      {/* <div className="mt-5">
        <div className="flex justify-between gap-2 overflow-x-auto pb-2 pl-[80px] md:pl-0">
          {relationButtons.map((relation) => {
            const isActive = activeRelations.includes(relation);
            return (
              <button
                key={relation}
                onClick={() => onToggleRelation?.(relation)}
                className={`rounded-full text-sm px-5 py-2 font-semibold transition-all duration-200 whitespace-nowrap shadow-[0px_4px_4px_rgba(105,56,239,0.37)] ${isActive
                    ? "bg-[#6938EF] text-white"
                    : "text-[#111] bg-white"
                  }`}
              >
                {relation}
              </button>
            );
          })}
        </div>
      </div> */}

      {/* 🎙️ Voice Modal */}
      {isVoiceModalOpen && (
        <FriendVoiceSendModal
          open={isVoiceModalOpen}
          onClose={handleCloseModal}
          userId={friendData?.user_id}
        />
      )}
    </div>
  );
});

export default function FriendInfo() {
  const { user_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const token = Cookies.get("auth_token");

  useEffect(() => {
    enforceBasicPlanNoOtherUsersAccess(user_id, navigate);
  }, [user_id, navigate]);

  const [friendData, setFriendData] = useState(null);
  const [visibleProfile, setVisibleProfile] = useState({});
  const [activeSection, setActiveSection] = useState(() => {
    try {
      return appStorage.getItem("active_section_friend") || "Personal Info";
    } catch (e) {
      return "Personal Info";
    }
  });
  const [showSidebar, setShowSidebar] = useState(true);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [paymentRequired, setPaymentRequired] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState("");
  const [showRemoveContactModal, setShowRemoveContactModal] = useState(false);
  const [showBlockContactModal, setShowBlockContactModal] = useState(false);
  const [blockingContact, setBlockingContact] = useState(false);
  const [removingContact, setRemovingContact] = useState(false);
  const [friendNickName, setFriendNickName] = useState("");
  const [showNickNameModal, setShowNickNameModal] = useState(false);
  const [nickNameInput, setNickNameInput] = useState("");
  const [loadingNickName, setLoadingNickName] = useState(false);
  const [savingNickName, setSavingNickName] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState({ birthday: null, anniversary: null });
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 900 : false));

  useEffect(() => {
    const mm = window.matchMedia("(max-width: 899px)");
    const onChange = (event) => setIsMobile(event.matches);

    if (mm.addEventListener) mm.addEventListener("change", onChange);
    else mm.addListener(onChange);

    setIsMobile(mm.matches);

    return () => {
      if (mm.removeEventListener) mm.removeEventListener("change", onChange);
      else mm.removeListener(onChange);
    };
  }, []);

  const extractNickName = useCallback((payload) => {
    if (!payload) return "";

    if (typeof payload === "string") return payload;

    if (Array.isArray(payload)) {
      return extractNickName(payload[0]);
    }

    if (typeof payload === "object") {
      return (
        payload.nick_name ||
        payload.nickname ||
        payload.nickName ||
        payload.name ||
        ""
      );
    }

    return "";
  }, []);

  const fetchFriendNickName = useCallback(async () => {
    if (!user_id || !token) return;

    try {
      setLoadingNickName(true);
      const body = new URLSearchParams();
      body.append("user_id", user_id);

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/friend-nick-name/get`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: body.toString(),
        }
      );

      const data = await res.json();
      if (data.status === "1") {
        const nickName = extractNickName(data.data);
        setFriendNickName(nickName || "");
      }
    } catch (err) {
      // Keep profile usable if nickname API fails.
      setFriendNickName("");
    } finally {
      setLoadingNickName(false);
    }
  }, [user_id, token, extractNickName]);

  const openNickNameModal = useCallback(() => {
    setNickNameInput(friendNickName || "");
    setShowNickNameModal(true);
  }, [friendNickName]);

  const sanitizeNickNameInput = useCallback((value) => {
    return value.replace(/[^A-Za-z0-9 _-]/g, "");
  }, []);

  const closeNickNameModal = useCallback(() => {
    setShowNickNameModal(false);
    setNickNameInput("");
  }, []);

  const handleSaveNickName = useCallback(async () => {
    if (!user_id || !token) {
      toast.error("Session expired. Please log in again.");
      navigate("/login");
      return;
    }

    const trimmedNickName = nickNameInput.trim();
    const hasExistingNickName = Boolean(friendNickName && friendNickName.trim());

    if (!trimmedNickName && !hasExistingNickName) {
      toast.error("Enter a nickname before updating");
      return;
    }

    try {
      setSavingNickName(true);
      const body = new URLSearchParams();
      body.append("user_id", user_id);
      body.append("nick_name", trimmedNickName);

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/friend-nick-name/set`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: body.toString(),
        }
      );

      const data = await res.json();
      if (data.status !== "1") {
        throw new Error(data.message || "Failed to update nickname");
      }

      toast.success(data.message || "Nickname updated");
      closeNickNameModal();
      await fetchFriendNickName();
    } catch (err) {
      toast.error(err?.message || "Failed to update nickname");
    } finally {
      setSavingNickName(false);
    }
  }, [user_id, token, nickNameInput, navigate, closeNickNameModal, fetchFriendNickName]);

  const handleBlockContact = useCallback(async () => {
    try {
      if (!token) {
        toast.error("Session expired. Please log in again.");
        navigate("/login");
        return;
      }

      setBlockingContact(true);

      const body = new URLSearchParams();
      body.append("user_id", user_id);

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/block`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: body.toString(),
      });

      const data = await res.json();
      if (data.status !== "1") {
        throw new Error(data.message || "Failed to block contact");
      }

      toast.success(data.message || "Contact blocked successfully");
      try {
        const from = location && location.state && location.state.from;
        if (from) {
          navigate(from);
        } else {
          navigate(FRIENDS_LIST_ROUTE, { replace: true });
        }
      } catch (e) {
        navigate(FRIENDS_LIST_ROUTE, { replace: true });
      }
    } catch (err) {
      toast.error(err?.message || "Failed to block contact");
    } finally {
      setBlockingContact(false);
    }
  }, [token, user_id, navigate, location]);

  const handleRemoveContact = useCallback(async () => {
    try {
      if (!token) {
        toast.error("Session expired. Please log in again.");
        navigate("/login");
        return;
      }

      setRemovingContact(true);

      const body = new URLSearchParams();
      body.append("user_id", user_id);

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/friend-request/remove`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: body.toString(),
      });

      const data = await res.json();
      if (data.status !== "1") {
        throw new Error(data.message || "Failed to remove contact");
      }

      toast.success(data.message || "Contact removed successfully");
      dispatchContactsChanged({ userId: user_id, action: "friend_removed" });
      try {
        const from = location && location.state && location.state.from;
        if (from) {
          navigate(from);
        } else {
          navigate(FRIENDS_LIST_ROUTE, { replace: true });
        }
      } catch (e) {
        navigate(FRIENDS_LIST_ROUTE, { replace: true });
      }
    } catch (err) {
      toast.error(err?.message || "Failed to remove contact");
    } finally {
      setRemovingContact(false);
    }
  }, [token, user_id, navigate, location]);
  // ✅ Fetch friend profile API
  useEffect(() => {
    const fetchFriendProfile = async () => {
      try {
        setLoadingProfile(true);
        const body = new URLSearchParams();
        body.append("user_id", user_id);

        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/friend-profilepermission/view`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: body.toString(),
          }
        );

        const data = await res.json();
        if (data.status === "1" && data.data?.[0]) {
          const friend = data.data[0];
          setFriendData({
            ...friend,
            your_relation: friend.your_relation || []
          });
          setVisibleProfile(friend.visible_profile || {});
        } else if (data.status === "5") {
          // Payment required state
          setPaymentRequired(true);
          setPaymentMessage(data.message || "Payment required to view this profile.");
          setFriendData(null);
          setVisibleProfile({});
        } else {
          toast.dismiss();
          toast.error(data.message || "Failed to fetch profile");
        }
      } catch (err) {
        toast.dismiss();
        toast.error(err.message);
      } finally {
        setLoadingProfile(false);
      }
    };

    if (user_id) fetchFriendProfile();
  }, [user_id, token]);

  useEffect(() => {
    if (!user_id || !token) {
      setUpcomingEvents({ birthday: null, anniversary: null });
      return;
    }

    const abortController = new AbortController();

    const fetchUpcomingEvents = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/birthdays/getall`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal,
        });

        const data = await res.json();
        if (!res.ok || data?.status !== "1") {
          setUpcomingEvents({ birthday: null, anniversary: null });
          return;
        }

        setUpcomingEvents(resolveFriendUpcomingEvents(data, user_id));
      } catch (err) {
        if (err.name !== "AbortError") {
          setUpcomingEvents({ birthday: null, anniversary: null });
        }
      }
    };

    fetchUpcomingEvents();

    return () => abortController.abort();
  }, [user_id, token]);

  useEffect(() => {
    fetchFriendNickName();
  }, [fetchFriendNickName]);

  // ✅ Generate dynamic sections
  const availableSections = useMemo(() => {
    const sections = [];
    if (visibleProfile) sections.push("Personal Info");
    if (visibleProfile) sections.push("Work Experience");
    if (visibleProfile) sections.push("Education");
    // sections.push("Notes"); // Always visible
    return sections;
  }, [visibleProfile]);

  const handleSectionClick = useCallback((section) => {
    setActiveSection(section);
    try { appStorage.setItem("active_section_friend", section); } catch (e) { }
    // On mobile, hide sidebar after selecting a section
    if (window.innerWidth < 900) {
      setShowSidebar(false);
    }
  }, []);

  const toggleSidebar = useCallback(() => {
    setShowSidebar((prev) => !prev);
  }, []);

  // Safe back handler: prefer explicit `location.state.from`, otherwise default to /contact
  const handleBack = useCallback(() => {
    try {
      const from = location && location.state && location.state.from;
      if (from) {
        navigate(from);
      } else {
        navigate(FRIENDS_LIST_ROUTE, { replace: true });
      }
    } catch (e) {
      navigate(FRIENDS_LIST_ROUTE, { replace: true });
    }
  }, [location, navigate]);

  // Toggle relation and send update to API
  const handleToggleRelation = useCallback(
    async (relation) => {
      if (!friendData) return;

      const apiRelation = relation;

      const prevRaw = friendData.your_friend_relations;
      const prev = Array.isArray(prevRaw)
        ? prevRaw
        : prevRaw
          ? String(prevRaw).split(",").map((s) => s.trim()).filter(Boolean)
          : [];

      const isActive = prev.includes(apiRelation);
      let newRelations;

      // Make 'Other' mutually exclusive: selecting Other clears others; selecting any other clears Other
      if (apiRelation === "Other") {
        if (isActive) {
          // unselect Other
          newRelations = prev.filter((r) => r !== apiRelation);
        } else {
          // select Other and remove all other relations
          newRelations = ["Other"];
        }
      } else {
        if (isActive) {
          // deselect this relation
          newRelations = prev.filter((r) => r !== apiRelation);
        } else {
          // select this relation and ensure 'Other' is removed
          newRelations = [...prev.filter((r) => r !== "Other"), apiRelation];
        }
      }

      setFriendData((p) => ({ ...(p || {}), your_friend_relations: newRelations }));

      try {
        const formData = new FormData();
        formData.append("user_id", user_id);
        formData.append("relations", newRelations.join(","));

        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/friend-profilepermission/set`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          }
        );

        const data = await res.json();
        if (data.status !== "1") {
          throw new Error(data.message || "Failed to update relations");
        }

        toast.success("Relations updated");
      } catch (err) {
        // revert on error
        setFriendData((p) => ({ ...(p || {}), your_friend_relations: prev }));
        toast.dismiss();
        toast.error(err?.message || "Failed to update relations");
      }
    },
    [friendData, user_id, token]
  );

    const handleUpdateRelationsBatch = useCallback(
      async (newRelations) => {
        if (!friendData) return;
        const prev = Array.isArray(friendData.your_friend_relations) ? friendData.your_friend_relations : [];
        setFriendData((p) => ({ ...(p || {}), your_friend_relations: newRelations }));
        try {
          const formData = new FormData();
          formData.append("user_id", user_id);
          formData.append("relations", (Array.isArray(newRelations) ? newRelations.join(",") : String(newRelations)));

          const res = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/friend-profilepermission/set`,
            {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
              body: formData,
            }
          );

          const data = await res.json();
          if (data.status !== "1") throw new Error(data.message || "Failed to update relations");
          toast.success("Relations updated");
        } catch (err) {
          setFriendData((p) => ({ ...(p || {}), your_friend_relations: prev }));
          toast.dismiss();
          toast.error(err?.message || "Failed to update relations");
        }
      },
      [friendData, user_id, token]
    );

  // ✅ Render active component based on visible_profile data
  const activeComponent = useMemo(() => {
    const map = {
      "Personal Info": (
        <FriendPersonalInfoPage
          data={visibleProfile?.personal_info || {} || []}
          homedata={visibleProfile?.home_address || [] || null}
          relations={friendData?.your_friend_relations || []}
          onUpdateRelations={handleUpdateRelationsBatch}
          friendName={friendData?.name || friendData?.nick_name || ''}
        />
      ),
      Address: (
        <FriendAddressPage data={visibleProfile?.address || []} />
      ),
      "Work Experience": (
        <FriendWorkExperiencePage data={visibleProfile?.work_experience || []} />
      ),
      Education: (
        <FriendEducationPage data={visibleProfile?.education || []} />
      ),
      Notes: <FriendNotesPage userId={user_id} />,
    };
    return map[activeSection] || <FriendPersonalInfoPage />;
  }, [activeSection, visibleProfile, user_id]);

  const pageShell = "h-full min-h-0 overflow-hidden bg-[#F3F2FF] p-4 min-[900px]:p-6";
  const pageInner = "mx-auto h-full min-h-0 flex flex-col";
  const pageFlex = "flex h-full min-h-0 flex-1 flex-col gap-4 min-[900px]:flex-row";
  const sidebarClasses = `${showSidebar ? "block" : "hidden"
    } min-[900px]:block h-full min-h-0 w-full min-[900px]:w-[360px] min-[900px]:min-w-[300px] min-[900px]:flex-shrink-0 space-y-4 overflow-y-auto overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:hidden`;
  const mainContentClasses = `${!showSidebar ? "block" : "hidden min-[900px]:block"
    } relative w-full min-[900px]:flex-1 min-w-0 min-h-0 h-full flex flex-col overflow-y-auto overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:hidden`;

  if (loadingProfile) {
    return (
      <div className={pageShell}>
        <div className={pageInner}>
          {!showSidebar && (
            <div className="min-[900px]:hidden mb-4 shrink-0">
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-100">
                <div className="w-5 h-5 bg-gray-200 rounded animate-pulse" />
                <span className="font-medium text-gray-400">Menu</span>
              </div>
            </div>
          )}

          <div className={pageFlex}>
            <div className="w-full min-[900px]:w-[360px] min-[900px]:min-w-[300px] min-[900px]:flex-shrink-0 space-y-4">
              <div className="p-4 rounded-2xl  animate-pulse space-y-4">
                <div className="flex items-center justify-between font-semibold">
                  <div className="h-5 w-24 bg-gray-200 rounded" />
                  <div className="h-5 w-5 bg-gray-200 rounded" />
                </div>
                <div className="flex items-start gap-4">
                  <div className="h-16 w-16 min-[900px]:h-24 min-[900px]:w-24 rounded-full bg-gray-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-32 bg-gray-200 rounded" />
                    <div className="h-4 w-40 bg-gray-200 rounded" />
                    <div className="h-4 w-28 bg-gray-200 rounded" />
                  </div>
                </div>
                <div className="flex justify-between gap-2 overflow-x-auto">
                  {Array.from({ length: 4 }).map((_, idx) => (
                    <div key={idx} className="h-8 w-20 rounded-full bg-gray-200" />
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <div key={idx} className="h-12 bg-white rounded-xl shadow-sm border border-gray-100 animate-pulse" />
                ))}
              </div>
            </div>

            <div className="w-full min-[900px]:flex-1 min-w-0">
              <div className="space-y-4 animate-pulse">
                <div className="h-6 w-40 bg-gray-200 rounded" />
                {Array.from({ length: 6 }).map((_, idx) => (
                  <div key={idx} className="h-14 bg-white rounded-xl border border-gray-100 shadow-sm" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (paymentRequired) {
    return (
      <div className={pageShell}>
        <div className={pageInner}>
          {!showSidebar && (
            <div className="min-[900px]:hidden mb-4 shrink-0">
              <button
                onClick={toggleSidebar}
                className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-100 hover:bg-gray-50 transition"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
                <span className="font-medium">Menu</span>
              </button>
            </div>
          )}

          <div className={pageFlex}>
            <div className="w-full">
              <div className="p-8 rounded-2xl  text-center min-h-[85vh] flex flex-col items-center justify-center">
                <div className="max-w-lg w-full">
                  <div className="mb-6 flex justify-center">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#EDE7FF] to-[#D9C9FF] flex items-center justify-center shadow-lg">
                      <img src="../no-data/no-personal.svg" alt="locked" className="w-20 h-20 opacity-80" />
                    </div>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Payment Required</h2>
                  <p className="text-gray-600 text-base leading-relaxed mb-8">{paymentMessage || "Please complete your subscription to continue."}</p>
                  <div className="flex flex-col gap-3">
                    <button
                      type="button"
                      className="submit-button w-full py-3 text-base font-semibold"
                      onClick={() => navigate("/payment")}
                    >
                      Complete Payment
                    </button>
                    <button
                      type="button"
                      className="normal-button w-full py-3 text-base font-semibold"
                      onClick={() => navigate(FRIENDS_LIST_ROUTE, { replace: true })}
                    >
                      Go Back
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={pageShell}>
      <div className={pageInner}>
        {!showSidebar && (
          <div className="min-[900px]:hidden mb-4 shrink-0">
            <button
              onClick={toggleSidebar}
              className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-100 hover:bg-gray-50 transition"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              <span className="font-medium">Menu</span>
            </button>
          </div>
        )}

        <div className={pageFlex}>
          {/* Sidebar */}
          <div className={sidebarClasses}>
            <UserProfile
              friendData={{
                ...(friendData || {}),
                nick_name: friendNickName || friendData?.nick_name || "",
              }}
              onToggleRelation={handleToggleRelation}
              handleSectionClick={handleSectionClick}
              onBackFromParent={handleBack}
              onOpenNickNameModal={openNickNameModal}
              loadingNickName={loadingNickName}
              upcomingEvents={upcomingEvents}
            />

            {/* Dynamic sections */}
            <div className="space-y-3">
              {availableSections.map((section) => (
                <SectionItem
                  key={section}
                  label={section}
                  icon={getSectionIcon(section)}
                  active={activeSection === section}
                  onClick={() => handleSectionClick(section)}
                />
              ))}
            </div>

            <div className="flex items-center justify-between ml-1">
              <button
                type="button"
                onClick={() => setShowRemoveContactModal(true)}
                disabled={removingContact || blockingContact}
                className="text-left text-red-500 underline font-medium flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <img src="../remove-contact.svg" className="h-5 w-5" alt="remove" />
                <span>{removingContact ? "Removing..." : "Remove Contact"}</span>
              </button>

              <button
                type="button"
                onClick={() => setShowBlockContactModal(true)}
                disabled={blockingContact || removingContact}
                className="text-left text-red-500 underline font-medium flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <img src="../remove-contact.svg" className="h-5 w-5" alt="block" />
                <span>{blockingContact ? "Blocking..." : "Block Contact"}</span>
              </button>
            </div>

          </div>
          {/* Main content */}
          <div className={mainContentClasses}>
            {activeComponent}
          </div>
        </div>
      </div>

      {showRemoveContactModal && (
        <div
          className="fixed inset-0 z-[22000] bg-black/45 flex items-center justify-center p-4"
          onClick={() => setShowRemoveContactModal(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-gray-900">Remove Contact</h3>
            <p className="text-sm text-gray-600 mt-2">
            Removing this contact stops messaging and info updates, but their contact card remains on PhoneBookPlus for easy re-adding.            </p>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setShowRemoveContactModal(false)}
                className="normal-button"
                disabled={removingContact}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  await handleRemoveContact();
                  setShowRemoveContactModal(false);
                }}
                disabled={removingContact}
                className="rounded-full bg-red-500 text-white px-4 py-2 font-semibold hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {removingContact ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showBlockContactModal && (
        <div
          className="fixed inset-0 z-[22000] bg-black/45 flex items-center justify-center p-4"
          onClick={() => setShowBlockContactModal(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-gray-900">Block Contact</h3>
            <p className="text-sm text-gray-600 mt-2">
            Blocking this contact completely removes them from your list and clears all chat history, but you can unblock them anytime to reconnect and start afresh            </p>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setShowBlockContactModal(false)}
                className="normal-button"
                disabled={blockingContact}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  await handleBlockContact();
                  setShowBlockContactModal(false);
                }}
                disabled={blockingContact}
                className="rounded-full bg-red-500 text-white px-4 py-2 font-semibold hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {blockingContact ? "Blocking..." : "Block"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNickNameModal && (
        <div
          className="fixed inset-0 z-[22000] bg-black/45 flex items-center justify-center p-4"
          onClick={closeNickNameModal}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-gray-900">Update Nickname</h3>
            <p className="text-sm text-gray-600 mt-2">Set a nickname for this friend.</p>

            <input
              type="text"
              value={nickNameInput}
              onChange={(e) => setNickNameInput(sanitizeNickNameInput(e.target.value))}
              placeholder="Enter nickname"
              className="mt-4 w-full rounded-xl border border-gray-300 px-4 py-3 outline-none focus:border-[#6938EF]"
              maxLength={50}
            />

            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={closeNickNameModal}
                className="normal-button"
                disabled={savingNickName}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveNickName}
                disabled={savingNickName || (!nickNameInput.trim() && !friendNickName.trim())}
                className="submit-button"
              >
                {savingNickName ? "Updating..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
